/**
 * P12 — `<meta charset>` head-budget rail.
 *
 * ## The defect this exists for
 *
 * The HTML spec requires a document's character encoding declaration to appear
 * **within the first 1024 bytes** of the resource; a parser that has not found
 * one by then stops looking and falls back to its locale default. The 2026-07-26
 * Iron Oak review measured the shipped fleet HTML and found:
 *
 * ```
 * <!doctype html>
 * <html lang="en">
 *   <head>
 *     <style data-dcs-motion-tokens="">:root { … 858 bytes of tokens … }</style>
 *     <meta charset="UTF-8" />     ← byte 910 of a 1024-byte window
 * ```
 *
 * The cms motion-token `<style>` was injected `head-prepend`, i.e. ABOVE the
 * site's own `<meta charset>`, leaving 90 bytes of headroom past the end of the
 * declaration. The SWA response also sends `Content-Type: text/html` with **no**
 * `charset` parameter, so there is no header-level backstop. One more head
 * injection — or a handful of new motion tokens — silently mojibakes every cms
 * SPA in the fleet, on every route, with nothing failing.
 *
 * That is the exact "a capability silently undone downstream, with nothing
 * noticing" class this module closes.
 *
 * ## The fix + the rail
 *
 * - **C-414 fixed the source.** `dcsMotionTokensStyleTag()` now injects `head`
 *   (append) rather than `head-prepend`, so the site's own declaration stays
 *   first in `<head>` and the block still lands above the bundled site CSS.
 *   This is the only fix that reaches VitePress sites, which never run the
 *   `emitStaticHtml` path below.
 * - {@link hoistCharsetMeta} moves the encoding declaration to the very top of
 *   `<head>` (inserting `<meta charset="utf-8" />` when the document has none).
 *   Pure, idempotent, and cascade-safe: only the charset meta moves, so the
 *   motion-token `<style>` keeps its position relative to the site's own CSS.
 *   Since C-414 this is a no-op on a stock cms shell — it stays as the backstop
 *   for hand-rolled shells and any future head injector.
 * - {@link checkCharsetBudget} measures the real BYTE offset of the declaration
 *   in the emitted document and reports headroom, so a regression is caught even
 *   if the hoist is ever bypassed.
 *
 * Everything here is framework-free and `fs`-free so both the Vite plugin path
 * and the unit tests share one implementation.
 */

/**
 * The spec's encoding-sniffing window. Per HTML Standard §13.2.3.2 ("determining
 * the character encoding") a prescan examines at most the first 1024 bytes.
 */
export const CHARSET_BUDGET_BYTES = 1024

/**
 * Recommended minimum slack between the declaration and the end of the window.
 * Below this the document is *technically* conformant but one head injection
 * away from breaking — the Iron Oak measurement (114 bytes) is precisely this
 * shape, which is why the rail reports headroom and not just pass/fail.
 */
export const CHARSET_HEADROOM_WARN_BYTES = 256

/** Matches `<meta charset="…">` (the modern form). */
const META_CHARSET_RE = /<meta\s+[^>]*\bcharset\s*=\s*["']?[^"'>\s]+["']?[^>]*>/i

/**
 * Matches the legacy `<meta http-equiv="Content-Type" content="…charset=…">`
 * form, which is an equally valid declaration for the prescan.
 */
const META_HTTP_EQUIV_CHARSET_RE =
  /<meta\s+[^>]*http-equiv\s*=\s*["']?content-type["']?[^>]*content\s*=\s*["'][^"']*charset=[^"']*["'][^>]*>/i

/** The canonical declaration this module inserts when a document has none. */
export const CANONICAL_CHARSET_META = '<meta charset="utf-8" />'

/** The result of measuring a document against the encoding-sniffing window. */
export interface CharsetBudgetResult {
  /** `true` when a declaration exists AND its last byte is inside the window. */
  ok: boolean
  /** Byte offset of the START of the declaration, or `-1` when absent. */
  offset: number
  /** Byte offset of the END of the declaration, or `-1` when absent. */
  endOffset: number
  /** `limitBytes - endOffset`; negative when the declaration is out of budget. */
  headroom: number
  /** The window used for the measurement (default {@link CHARSET_BUDGET_BYTES}). */
  limitBytes: number
  /** The matched declaration text, for reporting. */
  declaration: string | null
}

/**
 * Byte offset (UTF-8) of the character encoding declaration, or `-1`.
 *
 * Deliberately measured in BYTES, not characters: the spec's window is a byte
 * window, and a head containing non-ASCII (a `©` in a `<title>`, an em dash in a
 * meta description) makes the character index an under-estimate — the exact way
 * a naive check would report "fine" on a document that is not.
 */
export function findCharsetByteOffset(html: string): number {
  const match = META_CHARSET_RE.exec(html) ?? META_HTTP_EQUIV_CHARSET_RE.exec(html)
  if (!match) return -1
  return Buffer.byteLength(html.slice(0, match.index), 'utf8')
}

/**
 * Measure a document against the encoding-sniffing window.
 *
 * The declaration must END inside the window to count — a `<meta charset>` that
 * straddles byte 1024 is not seen by the prescan.
 */
export function checkCharsetBudget(
  html: string,
  options: { limitBytes?: number } = {}
): CharsetBudgetResult {
  const limitBytes = options.limitBytes ?? CHARSET_BUDGET_BYTES
  const match = META_CHARSET_RE.exec(html) ?? META_HTTP_EQUIV_CHARSET_RE.exec(html)
  if (!match) {
    return { ok: false, offset: -1, endOffset: -1, headroom: -limitBytes, limitBytes, declaration: null }
  }
  const offset = Buffer.byteLength(html.slice(0, match.index), 'utf8')
  const endOffset = offset + Buffer.byteLength(match[0], 'utf8')
  return {
    ok: endOffset <= limitBytes,
    offset,
    endOffset,
    headroom: limitBytes - endOffset,
    limitBytes,
    declaration: match[0],
  }
}

/**
 * Move the character encoding declaration to the first position inside `<head>`
 * — or insert the canonical one when the document has none.
 *
 * Guarantees, in order of importance:
 *   1. **Idempotent.** Re-running on already-hoisted HTML is a no-op (the
 *      declaration is already the first child of `<head>`), so it is safe to
 *      apply in `transformIndexHtml` AND again over the emitted per-route files.
 *   2. **Minimal.** Only the charset meta moves. The motion-token `<style>`, the
 *      site's CSS links, and the app script keep their relative order, so no
 *      cascade or execution-order behaviour changes.
 *   3. **Total.** A document with no `<head>` is returned unchanged rather than
 *      rewritten — the caller logs and no-ops (never corrupt a non-SPA shell).
 *
 * @param html - a full HTML document
 * @param options.insertIfMissing - insert {@link CANONICAL_CHARSET_META} when
 *   the document declares no encoding at all (default `true`). A cms SPA shell
 *   always has one; a hand-rolled template might not, and shipping *no*
 *   declaration is strictly worse than shipping a late one.
 */
export function hoistCharsetMeta(
  html: string,
  options: { insertIfMissing?: boolean } = {}
): string {
  const insertIfMissing = options.insertIfMissing ?? true

  const headOpen = /<head(\s[^>]*)?>/i.exec(html)
  if (!headOpen) return html
  const insertAt = headOpen.index + headOpen[0].length

  const match = META_CHARSET_RE.exec(html) ?? META_HTTP_EQUIV_CHARSET_RE.exec(html)

  if (!match) {
    if (!insertIfMissing) return html
    return html.slice(0, insertAt) + '\n    ' + CANONICAL_CHARSET_META + html.slice(insertAt)
  }

  // Already the first thing in <head> (allowing only whitespace between)? No-op,
  // which is what makes repeated application safe.
  const between = html.slice(insertAt, match.index)
  if (between.trim() === '') return html

  // A declaration outside <head> entirely (malformed input) is left alone —
  // moving it could change what the document means.
  const headClose = /<\/head\s*>/i.exec(html)
  if (headClose && match.index > headClose.index) return html

  const declaration = match[0]
  // Remove it from its current position, along with the whitespace-only line it
  // sat on, so the hoist does not leave a ragged blank line behind.
  const before = html.slice(0, match.index).replace(/[ \t]*$/, '')
  const after = html.slice(match.index + declaration.length).replace(/^[ \t]*\r?\n/, '')
  const withoutDeclaration = before + after

  // insertAt is unchanged: the declaration always sat AFTER <head> (guarded
  // above), so removing it cannot shift the opening tag's end offset.
  return (
    withoutDeclaration.slice(0, insertAt) +
    '\n    ' +
    declaration +
    withoutDeclaration.slice(insertAt)
  )
}

/** Thrown when an emitted document's encoding declaration is out of budget. */
export class CharsetBudgetError extends Error {
  readonly file: string
  readonly result: CharsetBudgetResult
  constructor(file: string, result: CharsetBudgetResult) {
    super(
      result.declaration === null
        ? `[dcs-seo] charset budget FAILED for ${file}: the document declares NO character ` +
            `encoding. Browsers fall back to a locale default and every non-ASCII byte can ` +
            `mojibake. Emit <meta charset="utf-8"> as the first child of <head>.`
        : `[dcs-seo] charset budget FAILED for ${file}: <meta charset> ends at byte ` +
            `${result.endOffset} of the ${result.limitBytes}-byte encoding-sniffing window ` +
            `(${result.headroom} bytes of headroom). The HTML parser stops looking after ` +
            `byte ${result.limitBytes}, so this document's encoding is not guaranteed to be ` +
            `detected. Move the declaration to the top of <head>.`
    )
    this.name = 'CharsetBudgetError'
    this.file = file
    this.result = result
  }
}

/**
 * Human-readable one-liner for the low-headroom (still-passing) case, so a
 * document creeping toward the cliff is visible in the build log before it falls
 * off it. Returns `null` when there is nothing worth saying.
 */
export function formatCharsetHeadroomWarning(
  file: string,
  result: CharsetBudgetResult,
  warnBelow = CHARSET_HEADROOM_WARN_BYTES
): string | null {
  if (!result.ok) return null
  if (result.headroom >= warnBelow) return null
  return (
    `[dcs-seo] charset budget: ${file} declares its encoding at byte ${result.offset} — ` +
    `only ${result.headroom} bytes of the ${result.limitBytes}-byte window remain. ` +
    `One more <head> injection breaks encoding detection on this site.`
  )
}
